import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import PopoverButton from '../../common/component/popover/PopoverButton';
import Table from '../../common/component/table/Table';
import {formatDate} from '../../common/util/DateUtil';
import {command} from '../../common/util/DispatchUtil';
import {sortCaseInsensitive} from '../../common/util/SortUtil';
import trainingApi from '../api/trainingApi';
import Disciplines from '../enums/Disciplines';
import Groupings from '../enums/Groupings';
import '../style/TrainingDetails.less';
import {msToTime, timeToMs} from '../TimeStringConversion';
import {noEntryOptional, parseTraining, translateEnum} from '../util/TrainingUtilities';
import buildTrainingColumns from './BuildTrainingColumns';
import FilterPopup from './FilterPopup';
import RunDialog from './RunDialog';
import SharePopup from './SharePopup';

function TrainingDetails() {
    const [t] = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector(state => state.account.isLoggedIn);
    const match = useRouteMatch();

    const allDefaultSorting = {
        id: 'idx',
        desc: false
    };
    const groupedDefaultSorting = {
        id: 'final',
        desc: false
    };
    const [currentTableSort, setCurrentTableSort] = useState(allDefaultSorting);
    const [initialSort, setInitialSort] = useState(allDefaultSorting);

    const [training, setTraining] = useState();
    const currentLanguage = useSelector(state => state.i18n.currentLanguage);

    const trainingShareToken = useSelector(state => !isLoggedIn ? state.training.shareToken : null);

    const [deletionSelection, selectForDeletion] = useState(undefined);
    const [editSelection, setEditSelection] = useState();
    const [showRunDialog, setShowRunDialog] = useState();
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);

    const [runs, setRuns] = useState([]);
    const [grouping, setGrouping] = useState(undefined);
    const [columns, setColumns] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [changedSorting, setChangedSorting] = useState(false);

    // Helper variable to skip first automatic table sort
    const [changedSortingInitial, setChangedSortingInitial] = useState(false);

    const allColumnOptions = [
        {
            accessor: 'idx',
            label: t('Training.IDX'),
            value: t('Training.IDX'),
            show: true
        },
        {
            accessor: 'run',
            label: t('Training.RUN'),
            value: t('Training.RUN'),
            show: true
        },
        {
            accessor: 'athleteShortName',
            label: t('Training.ATHLETE_SHORT_NAME'),
            value: t('Training.ATHLETE_SHORT_NAME'),
            show: true
        },
        {
            accessor: 'athleteFullName',
            label: t('Training.ATHLETE_FULL_NAME'),
            value: t('Training.ATHLETE_FULL_NAME'),
            show: true
        },
        {
            accessor: 'team',
            label: t('Training.TEAM'),
            value: t('Training.TEAM'),
            show: true
        },
        {
            accessor: 'interim',
            label: t('Training.INTERIM'),
            value: t('Training.INTERIM'),
            show: true
        },
        {
            accessor: 'section',
            label: t('Training.SECTION'),
            value: t('Training.SECTION'),
            show: true
        },
        {
            accessor: 'final',
            label: t('Training.FINAL'),
            value: t('Training.FINAL'),
            show: true
        },
        {
            accessor: 'notes',
            label: t('Training.NOTES'),
            value: t('Training.NOTES'),
            show: true
        },
        {
            accessor: 'plate',
            label: t('Training.PLATE'),
            value: t('Training.PLATE'),
            show: true
        },
        {
            accessor: 'ski',
            label: t('Training.SKI'),
            value: t('Training.SKI'),
            show: true
        },
        {
            accessor: 'binding',
            label: t('Training.BINDING'),
            value: t('Training.BINDING'),
            show: true
        },
        {
            accessor: 'boots',
            label: t('Training.BOOTS'),
            value: t('Training.BOOTS'),
            show: true
        }
    ];

    const [columnOptions, setColumnOptions] = useState(allColumnOptions);

    useEffect(() => {
        loadTraining();
    }, [location]);

    useEffect(() => {
        if (training && training.runs) {
            setColumns(buildTrainingColumns(columnOptions, training.numberOfInterims, handleDeleteRun, handleEditRun, !isLoggedIn));
            groupRuns([], Groupings.NONE, training.runs);
            setGrouping(Groupings.NONE);
        }
    }, [training?.id]);

    useEffect(() => {
        if (training) {
            handleGrouping(training);
        }
    }, [grouping, columns]);

    useEffect(() => {
        let newSorting;
        if (grouping === Groupings.NONE) {
            // If we switch to NONE grouping, always sort by idx
            newSorting = [{
                group: 'all',
                sorting: allDefaultSorting
            }];
        } else {
            // Keep initial sorting, update sorting groups
            newSorting = runs && runs.map((run) => {
                return ({
                    group: run.group,
                    sorting: currentTableSort
                });
            });
        }
        setSorting(newSorting);
    }, [runs]);

    useEffect(() => {
        ReactTooltip.rebuild();
    });

    useEffect(() => {
        if (training && training.runs) {
            setColumns(buildTrainingColumns(columnOptions, training.numberOfInterims, handleDeleteRun, handleEditRun, !isLoggedIn));
        }
    }, [columnOptions, currentLanguage]);

    function loadTraining() {
        if (match.params.id) {
            loadTrainingById(false);
        } else {
            loadTrainingByShareToken();
        }
    }

    const handleGrouping = (data) => {
        if (data && data.runs && data.runs.length > 0 && columns) {
            const rawRuns = data.runs;
            const groups = new Set();
            if (grouping === Groupings.ATHLETE) {
                const sortedRuns = rawRuns.sort((a, b) => {
                    return (sortCaseInsensitive(a.athlete.fullName + ' ' + a.athlete.shortName, b.athlete.fullName + ' ' + b.athlete.shortName) === 0)
                        ? (a.idx >= b.idx ? 1 : -1)
                        : (sortCaseInsensitive(a.athlete.fullName + ' ' + a.athlete.shortName, b.athlete.fullName + ' ' + b.athlete.shortName) > 0 ? 1 : -1);

                });

                sortedRuns.forEach((run) => {
                    groups.add(run.athlete.fullName + ' ' + run.athlete.shortName);
                });

            } else if (grouping === Groupings.RUN) {
                const sortedRuns = rawRuns.sort((a, b) => {
                    return (a.run >= b.run ? 1 : -1);
                });

                sortedRuns.forEach((run) => {
                    //TODO: run.run gets replaced by run.round when details are set
                    groups.add(run.run);
                });
            }
            groupRuns(groups, grouping, rawRuns);
        }
    };

    const loadTrainingById = (isReload) => {
        trainingApi.getTrainingById(match.params.id).then(response => {
            if (response.data.success) {
                const newTraining = parseTraining(response.data.result);
                if (isReload && newTraining && newTraining.runs) {
                    handleGrouping(newTraining);
                }
                setTraining(newTraining);
            }
        });
    };

    const loadTrainingByShareToken = () => {
        trainingApi.getSharedTrainingByToken(match.params.token).then(response => {
            if (response.data.success) {
                dispatch(command('SET_TRAINING_SHARE_TOKEN', match.params.token));
                const newTraining = parseTraining(response.data.result);
                setTraining(newTraining);
            }
        });
    };

    const handleEditRun = (selection) => {
        const runToSelect = {...training.runs.find(run => selection === run.id)};
        if (runToSelect.times) {
            // Set ms to formatted time for better change handling
            runToSelect.times = runToSelect.times.map(time => {
                return ({
                        ...time,
                        ms: msToTime(time.ms)
                    }
                );
            });
        } else {
            runToSelect.times = [];
        }

        setEditSelection(runToSelect);
        setShowRunDialog(true);
    };

    const handleDeleteRun = (selection) => {
        selectForDeletion(selection);
        setShowDeletionConfirmation(true);
    };

    const deleteRun = (selection) => {
        // If only one run remains delete the training instead
        if (training && training.runs.length === 1) {
            trainingApi.deleteTrainingById(training.id).then((response) => {
                if (response.data.success) {
                    history.push('/trainings');
                }
            });
        } else {
            trainingApi.deleteRunById(selection).then((response) => {
                if (response.data.success) {
                    loadTrainingById(true);
                }
            }).catch((err) => {
                console.log('error deleting training', err);
            });
        }
    };

    const formatRuns = (unformattedRuns, numberOfInterims) => {
        const dnf = 'DNF';
        const missed = '-';
        let finals = [];
        const interimMap = {};
        const numberOfRanksMap = {};
        let numberOfFinalRanks = 0;

        // convert time strings to ms; save them in arrays for each run
        unformattedRuns.forEach((run) => {
            run.times?.forEach((time) => {
                if (time?.ms > 0) {
                    if (time.sequenceIndex === 0) {
                        finals.push(time.ms);
                    } else {
                        if (!interimMap[`${(time.sequenceIndex - 1)}`]) {
                            interimMap[`${(time.sequenceIndex - 1)}`] = [];
                        }
                        interimMap[`${(time.sequenceIndex - 1)}`].push(time.ms);
                    }
                }
            });
        });

        // sort times
        finals = finals.sort((a, b) => {
            return a - b;
        });

        // sort the interim times of all runs for each interim categories
        Object.keys(interimMap).forEach((key) => {
            interimMap[key] = interimMap[key].sort((a, b) => {
                return a - b;
            });

            const slowestTime = Math.max(...interimMap[key]);

            // the max rank for a time is the first index of the slowest time
            numberOfRanksMap[key] = interimMap[key].indexOf(slowestTime) + 1;
        });

        const slowestFinalTime = Math.max(...finals);
        numberOfFinalRanks = finals.indexOf(slowestFinalTime) + 1;

        const fastestFinal = Math.min(...finals);
        // get the fastest interim time for each interim category
        const fastestInterims = Object.keys(interimMap).map((key) => {
            return Math.min(...interimMap[key]);
        });

        function setFinalDiff(finalProps) {
            if (finalProps.dnf) {
                return 'DNF';
            } else if (finalProps.missed) {
                return '-';
            } else {
                return (finalProps.diffPositive ? '+' : '-') + msToTime(finalProps.diff, true);
            }
        }

        return unformattedRuns.map((run) => {
            const formatTimeObj = (r, seqIdx) => {
                const obj = r.times?.find((time) => {
                    return time.sequenceIndex === seqIdx;
                });
                let timeEntry = seqIdx === 0 ? dnf : missed;
                if (obj) {
                    if (obj.missed) {
                        timeEntry = missed;
                    } else {
                        timeEntry = obj.ms;
                    }
                }
                let rank = missed;

                if (seqIdx === 0) {
                    rank = obj?.missed ? missed : dnf;
                }

                let diff = 0;
                if (timeEntry !== dnf && timeEntry !== missed) {
                    rank = (seqIdx === 0 ? finals.indexOf(timeEntry) + 1 : interimMap[`${(seqIdx - 1)}`]?.indexOf(timeEntry) + 1);
                    diff = (seqIdx === 0 ? timeEntry - fastestFinal : timeEntry - fastestInterims[seqIdx - 1]);
                }
                const diffPositive = diff > 0;
                return {
                    time: typeof timeEntry !== 'string' && Number(timeEntry) !== 0 ? msToTime(timeEntry) : timeEntry,
                    rank: rank,
                    diff: timeEntry !== dnf && timeEntry !== missed ? Math.abs(diff) : timeEntry,
                    diffPositive: diffPositive,
                    missed: timeEntry === missed,
                    dnf: timeEntry === dnf,
                    max_rank: seqIdx === 0 ? numberOfFinalRanks : numberOfRanksMap[`${(seqIdx - 1)}`]
                };
            };

            const finalProps = formatTimeObj(run, 0);

            const res = {
                    id: run.id,
                    idx: run.idx,
                    rank: '' + finalProps.rank,
                    athleteFullName: run.athlete?.fullName,
                    athleteShortName: run.athlete?.shortName,
                    run: run.run,
                    team: run.team,
                    notes: run.notes,
                    ski: run.ski,
                    binding: run.binding,
                    boots: run.boots,
                    plate: run.plate,
                    final: finalProps.time,
                    final_diff: setFinalDiff(finalProps),
                    final_rank: `${finalProps.rank}`,
                    max_final_rank: finalProps.max_rank
                }
            ;
            // add interims
            let interimMemory = 0;
            let interimMemoryMissed = false;
            for (let i = 1; i <= numberOfInterims; ++i) {
                const interimProps = formatTimeObj(run, i);
                res[`interim${i}_rank`] = `${interimProps.rank}`;
                res[`interim${i}`] = interimProps.time;
                if (interimProps.missed) {
                    res[`interim${i}_diff`] = missed;
                } else {
                    res[`interim${i}_diff`] = (interimProps.diffPositive ? '+' : '-') + msToTime(Math.abs(Number(interimProps.diff)), true);
                }
                res[`section${i}`] = interimProps.time !== dnf && !interimProps.missed && interimMemory >= 0 ?
                    msToTime(timeToMs(interimProps.time) - interimMemory) :
                    interimProps.missed || interimMemoryMissed ? missed : dnf;
                interimMemory = interimProps.time !== dnf && !interimProps.missed ? timeToMs(interimProps.time) : -1;
                interimMemoryMissed = interimProps.missed;
                res[`interim${i}_max_rank`] = interimProps.max_rank;
            }
            res[`section_final`] = res.final !== dnf && res.rank !== missed && interimMemory >= 0 ? msToTime(timeToMs(res.final) - interimMemory) :
                missed;
            return res;
        });
    };

    const groupRuns = (groups, groupName, rawRuns) => {
        const groupedRuns = [];
        if (groups.size === 0 || !groupName || groupName === Groupings.NONE) {
            groupedRuns.push({
                data: formatRuns(rawRuns, training.numberOfInterims),
                columns: columns
            });
        } else if (groups.size === 0 || !groupName || groupName === Groupings.ATHLETE) {
            groups.forEach((category) => {
                // filter data
                const filteredRuns = rawRuns.filter((run) => {
                    //little hack to merge two strings to one so we have a primitive value (needed for equality function in Set)
                    // and we group athletes correctly
                    return ((run.athlete.fullName + ' ' + run.athlete.shortName) === category);
                });
                const filteredFormattedRuns = formatRuns(filteredRuns, training.numberOfInterims);
                // define table objects
                groupedRuns.push({
                    data: filteredFormattedRuns,
                    columns: columns,
                    group: category
                });
            });
        } else {
            groups.forEach((category) => {
                // filter data
                const filteredRuns = rawRuns.filter((run) => {
                    return run[groupName] === category;
                });
                const filteredFormattedRuns = formatRuns(filteredRuns, training.numberOfInterims);
                // define table objects
                groupedRuns.push({
                    data: filteredFormattedRuns,
                    columns: columns,
                    group: category
                });
            });
        }
        setRuns([...new Set(groupedRuns)]);
    };

    const checkForUnevenInterimHeader = (header) => {
        return header.includes(t('Training.INTERIM')) && parseInt(header.split(' ')[1]) % 2 === 1;
    };

    const headerStyle = (header, parentHeader) => {
        if (parentHeader) {
            if (parentHeader === t('Training.FINAL') || checkForUnevenInterimHeader(parentHeader)) {
                return {
                    backgroundColor: '#3E5B8E',
                    color: 'white'
                };
            }
        } else {
            if (header === t('Training.FINAL') || checkForUnevenInterimHeader(header)) {
                return {
                    backgroundColor: '#3E5B8E',
                    color: '#89B1F9'
                };
            } else {
                return {
                    backgroundColor: '#354E7A',
                    color: '#89B1F9'
                };
            }
        }
        return {
            backgroundColor: '#354E7A',
            color: 'white'
        };
    };

    const toggleColumnVisibility = (value) => {
        const cols = [...columnOptions];
        if (value === 'all') {
            cols.forEach(col => col.show = true);
        } else if (value === 'none') {
            cols.forEach(col => col.show = false);
        } else {
            if (value.id < cols.length) {
                cols[value.id].show = !cols[value.id].show;
            }
        }
        setColumnOptions(Object.assign([], columnOptions, cols));
    };

    const downloadExcelFile = () => {

        const options = {};
        columnOptions.forEach(option => options[option.accessor] = option.show);
        options['trainingId'] = training.id;

        const exportData = {
            trainingId: options.trainingId,
            run: options.run,
            athleteFullName: options.athleteFullName,
            athleteShortName: options.athleteShortName,
            team: options.team,
            section: options.section,
            interim: options.interim,
            finish: options.final,
            notes: options.notes,
            ski: options.ski,
            boots: options.boots,
            binding: options.binding,
            plate: options.plate,
            sorting: sorting
        };

        if (!isLoggedIn) {
            exportData.shareToken = trainingShareToken;
        }

        trainingApi.downloadTraining(exportData, grouping.toUpperCase()).then(((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/vnd.ms-excel'}));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'training.xlsx');
            document.body.appendChild(link);
            link.click();
        })).catch((error) => console.log(error));
    };

    const detailListToString = (details) => {
        if (details && details.length > 0) {
            return details?.map(entry => `${translateEnum(noEntryOptional(entry))}`).join(', ');
        } else {
            return `${translateEnum(noEntryOptional('NO_ENTRY'))}`;
        }
    };

    function createDistanceEntries() {
        return detailListToString(training.gateDistance) + (training.gateDistance && training.gateDistance.length > 0 ? ' m' : '');
    }

    const getFilterPopup = () => {
        return (
            <FilterPopup
                options={allColumnOptions}
                values={columnOptions}
                onChange={toggleColumnVisibility}
                selectAllOptions={() => toggleColumnVisibility('all')}
                deselectAllOptions={() => toggleColumnVisibility('none')}
            />
        );
    };

    const getSharePopup = () => {
        return (
            <SharePopup
                trainingId={training?.id}
            />
        );
    };

    const EditTrainingDetails = () => {
        return (
            isLoggedIn ?
                <button
                    className="btn btn-transparent-secondary mb-3"
                    id="btn-edit-training-details"
                    onClick={() => {
                        history.push(`/training-edit/${training.id}`);
                    }}>
                    <FontAwesomeIcon icon="pencil-alt" className="mr-2"/>
                    {t('Training.EDIT_TRAINING_DETAILS')}
                </button>
                : <span></span>
        );
    };

    function openNewRunDialog() {
        setEditSelection(null);
        setShowRunDialog(true);
    }

    function calculateNextIndex() {
        let maxIndex = 0;
        for (const run of training?.runs) {
            maxIndex = Math.max(maxIndex, run?.idx);
        }
        return maxIndex + 1;
    }

    function handleTableSortChange(tableGroup, header) {
        if (changedSortingInitial) {
            if (grouping === Groupings.NONE) {
                tableGroup = 'all';
            } else {
                tableGroup = tableGroup && typeof tableGroup === 'string' ? tableGroup.replace('undefined ', '') : tableGroup;
            }
            const updatedSorting = [...sorting];
            const sortGroup = updatedSorting.find(sortElement => sortElement.group === tableGroup);
            if (sortGroup) {
                sortGroup.sorting = header && header[0];
            } else {
                updatedSorting.push({
                    group: tableGroup,
                    sorting: header && header[0]
                });
            }
            setSorting(updatedSorting);

            if (grouping === Groupings.NONE) {
                if (header && header[0]) {
                    setCurrentTableSort(header[0]);
                    setInitialSort(header[0]);
                } else {
                    setCurrentTableSort(allDefaultSorting);
                    setInitialSort(allDefaultSorting);
                }
            } else {
                // Reset current sort to prevent it from overriding the table sort
                setCurrentTableSort(null);
            }
        }

        if (changedSortingInitial) {
            setChangedSorting(true);
        }
        setChangedSortingInitial(true);
    }

    if (!training) {
        return (<>
            {t('Training.SHARED_TRAINING_NOT_FOUND')}
        </>);
    }

    function handleGroupingChange(value) {
        if (value === Groupings.NONE) {
            // If we switch to NONE grouping, always sort by idx
            setCurrentTableSort(allDefaultSorting);
            setInitialSort(allDefaultSorting);
        } else if (grouping !== Groupings.NONE || !changedSorting) {
            // if we switch between ATHLETE and RUN groupings,
            // or we come from NONE grouping but have no changes, sort by end time
            setCurrentTableSort(groupedDefaultSorting);
            setInitialSort(groupedDefaultSorting);
        }
        setChangedSorting(false);
        setChangedSortingInitial(false);
        setGrouping(value);
    }

    return (
        <>
            <ReactTooltip type="dark" multiline={true}/>
            {
                isLoggedIn &&
                <div className="mb-5">
                    <button
                        className="btn btn-primary"
                        onClick={() => history.push('/trainings')}>
                        <FontAwesomeIcon icon="arrow-left" className="mr-2"/>
                        {t('Button.GO_BACK_TO_OVERVIEW')}
                    </button>
                </div>
            }

            <h2 className="mb-4">
                {(training ? translateEnum(training.discipline) : t('Common.NO_ENTRY'))
                + ` ${t('Training.TITLE')} ${t('Particle.DATE_ON')} ${formatDate(currentLanguage, (training?.date || ''))}`}
            </h2>
            <div className="mt-5 mb-2">
                <EditTrainingDetails/>
                {
                    training &&
                    <div className="training-info">
                        <div className="info-column">
                            <p>{t('Training.COURSE_SETTER')}</p>
                            <p className="training-entries">{noEntryOptional(training.courseSetter)}</p>
                            <p>{t('Training.LOCATION')}</p>
                            <p className="training-entries">{noEntryOptional(training.slope?.location)}</p>
                            <p>{t('Training.SLOPE')}</p>
                            <p className="training-entries">{noEntryOptional(training.slope?.name)}</p>
                            <p>{t('Training.SNOW_TYPE')}</p>
                            <p className="training-entries">{detailListToString(training.snowType)}</p>
                            <p>{t('Training.SLOPE_CONDITION')}</p>
                            <p className="training-entries">{detailListToString(training.slopeCondition)}</p>
                            <p>{t('Training.TERRAIN')}</p>
                            <p className="training-entries">{detailListToString(training.terrain)}</p>
                        </div>
                        <div className="info-column">
                            <p>{t('Training.COURSE_SETUP')}</p>
                            <p className="training-entries">{detailListToString(training.courseSetting)}</p>
                            {
                                (training.discipline === Disciplines.SLALOM || training.discipline === Disciplines.PARALLEL_GIANT_SLALOM)
                                    ?
                                    <>
                                        <p>{t('Training.SLALOM_DISTANCE')}</p>
                                        <p className="training-entries">{createDistanceEntries()}</p>
                                    </>
                                    :
                                    <>
                                        <p>{t('Training.GATE_DISTANCE')}</p>
                                        <p className="training-entries">{createDistanceEntries()}</p>
                                    </>
                            }

                            <p>{t('Training.NUMBER_OF_GATES')}</p>
                            <p className="training-entries">{noEntryOptional(training.gateCount)}</p>
                            <p>{t('Training.CHANGES_OF_DIRECTION')}</p>
                            <p className="training-entries">{noEntryOptional(training.turnaround)}</p>
                            <p>{t('Training.RUNS')}</p>
                            <p className="training-entries">{noEntryOptional(training.numberOfRuns)}</p>
                            <p>{t('Training.RUNTIME')}</p>
                            <p className="training-entries">{noEntryOptional(msToTime(training.avgRunTime))}</p>
                        </div>
                    </div>
                }
            </div>
            <div>
                {/* table options */
                    <div className="table-options">
                        <div className="btn-group btn  mb-5 mt-4">
                            <button
                                className={grouping === Groupings.NONE ? 'selected_theme_here' : 'not_selected_theme_here'}
                                onClick={() => handleGroupingChange(Groupings.NONE)}>
                                {t('Button.OVERVIEW')}
                            </button>
                            <button
                                className={grouping === Groupings.RUN ? 'selected_theme_here' : 'not_selected_theme_here'}
                                onClick={() => handleGroupingChange(Groupings.RUN)}>
                                {t('Button.RUNS')}
                            </button>
                            <button
                                className={grouping === Groupings.ATHLETE ? 'selected_theme_here' : 'not_selected_theme_here'}
                                onClick={() => handleGroupingChange(Groupings.ATHLETE)}>
                                {t('Button.ATHLETE')}
                            </button>
                        </div>
                        <div className="modal-footer">
                            {
                                isLoggedIn &&
                                <div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={openNewRunDialog}>
                                        <FontAwesomeIcon icon="plus" className="mr-2"/>
                                        {t('Button.RUN_ADD')}
                                    </button>
                                </div>
                            }
                            <div className="flex-split-item">
                                {
                                    isLoggedIn ?
                                        <PopoverButton
                                            content={getSharePopup()}
                                            placement={'left'}
                                            className={'btn btn-outline-primary popover-button'}
                                            buttonTitle={t('Button.SHARE')}
                                            buttonIcon={<FontAwesomeIcon icon="arrow-up" className="mr-2"/>}
                                        />
                                        : <span/>
                                }
                            </div>
                            <PopoverButton
                                content={getFilterPopup()}
                                placement={'left'}
                                className={'btn btn-outline-primary popover-button'}
                                buttonTitle={t('Button.COLUMNS')}
                                buttonIcon={<FontAwesomeIcon icon="filter" className="mr-2"/>}
                            />
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    downloadExcelFile();
                                }}>
                                <FontAwesomeIcon icon="arrow-down" className="mr-2"/>
                                {t('Button.DOWNLOAD')}
                            </button>
                        </div>
                    </div>
                }
                <div className="table-container" id="training-table-detail">
                    {
                        grouping && runs.length > 0 &&
                        runs.map((table, index) => {
                            return (
                                <div key={index} className="sub-table">
                                    {
                                        table.data && table.columns &&
                                        <Table
                                            key={index}
                                            data={table.data}
                                            columns={table.columns}
                                            pageSize={200}
                                            initialSort={[initialSort]}
                                            sortable={true}
                                            onSortChange={(header) => handleTableSortChange(table.group, header)}
                                            getColumnProps={(column) => ({
                                                style: headerStyle(column.Header, (column.parent ? column.parent.Header : undefined))
                                            })}
                                            sortFromOutside={[currentTableSort]}
                                        />
                                    }
                                </div>
                            );
                        })
                    }
                </div>
                {
                    isLoggedIn && showDeletionConfirmation &&
                    <ConfirmationDialog
                        title={t('Run.DELETE_MODAL_TITLE')}
                        open={showDeletionConfirmation}
                        description={training && training.runs.length === 1 ? t('Run.DELETE_MODAL_QUESTION_LAST_RUN') : t('Run.DELETE_MODAL_QUESTION')}
                        accept={() => {
                            deleteRun(deletionSelection);
                            selectForDeletion(undefined);
                            setShowDeletionConfirmation(false);

                        }}
                        cancel={() => {
                            selectForDeletion(undefined);
                            setShowDeletionConfirmation(false);
                        }}
                        acceptLabel={t('Button.DELETE')}
                    />
                }
                <RunDialog
                    open={showRunDialog}
                    onSave={() => {
                        setShowRunDialog(false);
                        setEditSelection(null);
                        loadTrainingById(true);
                    }}
                    onCancel={() => {
                        setShowRunDialog(false);
                        setEditSelection(null);
                    }}
                    run={editSelection}
                    numberOfInterims={training.numberOfInterims}
                    trainingId={training.id}
                    nextIndex={calculateNextIndex()}
                />
            </div>
        </>
    );
}

export default TrainingDetails;
